import React, { useState } from 'react';
import * as PropTypes from 'prop-types';
import Box from '@material-ui/core/Box';
import { Divider, Grid } from '@material-ui/core';
import Select from '../../form/components/Select';
import TextInput from '../../form/components/TextInput';
import CountrySelect from '../../form/components/specialized/CountrySelect';
import DateInput from '../../form/components/DateInput';
import PhoneNumbersInput, {
    getEmptyPhoneNumber,
} from '../../form/components/specialized/PhoneNumbersInput';
import FormPrefiller from '../../form/components/FormPrefiller';
import BaseResourceAutocomplete from '../../form/components/base/autocomplete/BaseResourceAutocomplete';
import { ZIPCODE_RESOURCE } from '../../api/resources';
import Typography from '../../mui/Components/Typography';
import { useFieldFast } from '../../form/hooks';
import { makeContractsByCustomerIdSelector } from '../../contracts/contractsSlice';
import { useMakeSelector } from '../../store/hooks';
import Alert from '@material-ui/lab/Alert';

const AutocompleteOption = ({ option }) => {
    return (
        <Box ml={2}>
            <Grid container spacing={2} alignItems="center">
                <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary">
                        Ortsvorschlag
                    </Typography>
                    <Box>
                        <Typography variant="inherit">{option.city}</Typography>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

const renderOption = (option) => <AutocompleteOption option={option} />;

const getOptionLabel = (option) => {
    return typeof option === 'string' ? option : option?.zipCode || '';
};

export const CONTACT_INITIAL_VALUES = {
    customerId: '',
    salutation: '',
    title: '',
    firstname: '',
    lastname: '',
    company: '',
    address: '',
    zip: '',
    city: '',
    country: 'deu',
    email: '',
    type: '',
    bornAt: null,
    birthdayCardSentAt: null,
    diedAt: null,
    condolencesSentAt: null,
    phoneNumbers: [getEmptyPhoneNumber()],
};

const ContactFormBody = ({ simple, prefix }) => {
    const [, , { setValue: setValueCity }] = useFieldFast((prefix ? prefix + '.' : '') + 'city');
    const [{ value: valueZip }, , { setValue: setValueZip }] = useFieldFast(
        (prefix ? prefix + '.' : '') + 'zip'
    );

    const customerIdField = prefix ? prefix + '.customerId' : 'customerId';
    const [{ value: customerId }] = useFieldFast(customerIdField);
    const [{ value: valueDiedAt }] = useFieldFast((prefix ? prefix + '.' : '') + 'diedAt');
    //console.log("valueDiedAt => ",valueDiedAt);

    // get the latest contracts of this customer
    const contract = useMakeSelector(makeContractsByCustomerIdSelector, customerId);
    // get all pzh contracts that are not terminated today
    const pzhContracts = contract.filter((c) => c.type === 'pzh' && (c.terminatedAt === null || new Date(c.terminatedAt).toDateString() === new Date().toDateString()) );
    // get the latest pzh contract
    const latestPzhContract = pzhContracts.sort((a,b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
    //console.log("contract => ",contract);

    const [showWarningMissingContractCancelation, setShowWarningMissingContractCancelation] = useState(false);
    const [{ value: valueCountry }] = useFieldFast((prefix ? prefix + '.' : '') + 'country');
    const [zipOption, setZipOption] = useState(null);
    const handleChange = (event, newValue) => {
        setZipOption(newValue);

        if (typeof newValue === 'string') {
            setValueZip(newValue);
        } else if (newValue) {
            setValueCity(newValue.city);
            setValueZip(newValue.zipCode);
        }
    };
    const handleInputChange = (event, newValue, reason) => {
        if (reason === 'input' || reason === 'clear') {
            setValueZip(newValue);
            if (reason === 'clear') {
                setZipOption(null);
            }
        }
    };

    if (latestPzhContract && (latestPzhContract.terminatedAt === null || latestPzhContract.terminationReason !== 'death') ) {
       // show warning if diedAt is set but contract is not cancelled
       if (valueDiedAt) {
           if (!showWarningMissingContractCancelation) {
               setShowWarningMissingContractCancelation(true);
           }
       } else {
           if (showWarningMissingContractCancelation) {
               setShowWarningMissingContractCancelation(false);
           }
       }
    }

    return (
        <>
            <FormPrefiller
                values={{
                    salutation: 'female',
                    title: 'Title',
                    firstname: 'Firstname',
                    lastname: 'Lastname',
                    company: 'Company',
                    address: 'Address',
                    zip: '12345',
                    city: 'City',
                    country: 'deu',
                    email: 'projekte@pflegezuhause.info',
                    phoneNumbers: [],
                }}
                prefix={prefix}
            />

            <Grid container spacing={2}>
                <Grid item xs={4}>
                    <Select label="Anrede" name="salutation" prefix={prefix} fullWidth />
                </Grid>
                <Grid item xs={4}>
                    <TextInput name="title" prefix={prefix} label="Titel" fullWidth />
                </Grid>

                <Grid item xs={6}>
                    <TextInput name="firstname" prefix={prefix} label="Vorname" fullWidth />
                </Grid>
                <Grid item xs={6}>
                    <TextInput name="lastname" prefix={prefix} label="Nachname" fullWidth />
                </Grid>

                <Grid item xs={12}>
                    <Divider />
                </Grid>

                <Grid item xs={12}>
                    <TextInput name="company" prefix={prefix} label="Firma" fullWidth />
                </Grid>

                <Grid item xs={12}>
                    <TextInput name="address" prefix={prefix} label="Adresse" fullWidth />
                </Grid>

                <Grid item xs={3}>
                    {valueCountry !== 'deu' ? (
                        <TextInput name="zip" prefix={prefix} label="PLZ" fullWidth />
                    ) : (
                        <BaseResourceAutocomplete
                            value={zipOption}
                            inputValue={valueZip || ''}
                            onChange={handleChange}
                            onInputChange={handleInputChange}
                            resource={ZIPCODE_RESOURCE}
                            name="zipAC"
                            label="PLZ"
                            queryField="zipCode"
                            threshold={4}
                            renderOption={renderOption}
                            getOptionLabel={getOptionLabel}
                            freeSolo
                            fullWidth
                        />
                    )}
                </Grid>
                <Grid item xs={6}>
                    <TextInput name="city" prefix={prefix} label="Ort" fullWidth />
                    {/* component: CityWithSuggestions, zipSearchResults, zipSearchInProgress */}
                </Grid>
                <Grid item xs={3}>
                    <CountrySelect name="country" prefix={prefix} label="Land" fullWidth />
                </Grid>

                <Grid item xs={12}>
                    <Divider />
                </Grid>

                <Grid item xs={12}>
                    <TextInput type="email" name="email" prefix={prefix} label="E-Mail" fullWidth />
                </Grid>

                {!simple && (
                    <>
                        <Grid item xs={12}>
                            <Divider />
                        </Grid>
                        <Grid item xs={4}>
                            <DateInput name="bornAt" prefix={prefix} label="Geboren am" fullWidth />
                        </Grid>
                        <Grid item xs={4}>
                            <DateInput
                                name="birthdayCardSentAt"
                                prefix={prefix}
                                label="Geburtstagskarte"
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={4} />
                        {showWarningMissingContractCancelation&& (
                            <Grid item xs={12}>
                                <Alert severity="warning">
                                    Bitte den Kündigungsprozess mit dem Grund der Kündigung "Patient/in verstorben" starten!
                                </Alert>
                            </Grid>
                        )}
                        <Grid item xs={4}>
                            <DateInput
                                name="diedAt"
                                prefix={prefix}
                                label="Verstorben am"
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <DateInput
                                name="condolencesSentAt"
                                prefix={prefix}
                                label="Trauerkarte"
                                fullWidth
                            />
                        </Grid>
                    </>
                )}
                <Grid item xs={12}>
                    <Divider />
                </Grid>

                <Grid item xs={12}>
                    <PhoneNumbersInput name="phoneNumbers" prefix={prefix} allowAdd min={1} />
                </Grid>
            </Grid>
        </>
    );
};

ContactFormBody.propTypes = {
    simple: PropTypes.bool,
    prefix: PropTypes.string,
};

ContactFormBody.defaultProps = {
    simple: false,
    prefix: null,
};

export default ContactFormBody;
